import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ClipboardList, Clock, CheckCircle, AlertTriangle,
  Plus, ArrowRight, Zap, TrendingUp, TrendingDown,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDate } from "@/utils/formatDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_CONFIG = [
  { key: "total",            label: "Total Received",  icon: ClipboardList, accent: "border-l-lime-500",    num: "text-lime-700",    bg: "bg-lime-50",    icon_c: "text-lime-600",    testId: "stat-total" },
  { key: "in_progress",     label: "In Progress",      icon: Clock,         accent: "border-l-violet-500",  num: "text-violet-700",  bg: "bg-violet-50",  icon_c: "text-violet-600",  testId: "stat-in-progress" },
  { key: "waiting_approval",label: "Waiting Approval", icon: AlertTriangle, accent: "border-l-amber-500",   num: "text-amber-700",   bg: "bg-amber-50",   icon_c: "text-amber-600",   testId: "stat-waiting" },
  { key: "completed",       label: "Completed",        icon: CheckCircle,   accent: "border-l-emerald-500", num: "text-emerald-700", bg: "bg-emerald-50", icon_c: "text-emerald-600", testId: "stat-completed" },
];

const AVATAR_COLORS = ["bg-lime-600","bg-violet-500","bg-emerald-500","bg-amber-500","bg-rose-500","bg-cyan-600"];

function getInitials(name = "") {
  return name.trim().split(/\s+/).map((w) => w[0] || "").join("").slice(0, 2).toUpperCase() || "?";
}

function formatMonth(ym) {
  if (!ym) return "";
  const [year, month] = ym.split("-");
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[parseInt(month, 10) - 1]} '${year.slice(2)}`;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Build chart data
  const monthlyData = (stats?.monthly_volume ?? []).map((d) => ({
    month: formatMonth(d._id),
    count: d.count,
  }));
  const lastCount = monthlyData[monthlyData.length - 1]?.count ?? 0;
  const prevCount = monthlyData[monthlyData.length - 2]?.count ?? 0;
  const growth = prevCount > 0 ? Math.round(((lastCount - prevCount) / prevCount) * 100) : null;

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div>
            <h1
              className="text-xl font-bold text-gray-900 tracking-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Service Centre Dashboard
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">{today}</p>
          </div>
          <Button
            onClick={() => navigate("/tickets/new")}
            data-testid="dashboard-new-ticket-btn"
            className="bg-lime-600 hover:bg-lime-700 text-white shadow-sm shadow-lime-900/20"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Ticket
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {STAT_CONFIG.map(({ key, label, icon: Icon, accent, num, bg, icon_c, testId }) => (
            <div
              key={key}
              data-testid={testId}
              onClick={() => navigate("/tickets")}
              className={`bg-white rounded-2xl border border-gray-200 border-l-4 ${accent} p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer`}
            >
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</p>
                <div className={`${bg} p-2 rounded-xl`}>
                  <Icon className={`w-4 h-4 ${icon_c}`} />
                </div>
              </div>
              {loading ? (
                <div className="h-9 w-16 bg-gray-100 rounded-lg animate-pulse" />
              ) : (
                <p className={`text-4xl font-black tracking-tight ${num}`}>{stats?.[key] ?? 0}</p>
              )}
            </div>
          ))}
        </div>

        {/* ── Monthly Volume Trend Chart ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2
                className="font-bold text-gray-900 text-base"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Monthly Ticket Volume
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Service tickets received per month
              </p>
            </div>
            <div className="flex items-center gap-3">
              {growth !== null && (
                <div
                  className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${
                    growth >= 0 ? "bg-lime-50 text-lime-700" : "bg-red-50 text-red-600"
                  }`}
                >
                  {growth >= 0 ? (
                    <TrendingUp className="w-3.5 h-3.5" />
                  ) : (
                    <TrendingDown className="w-3.5 h-3.5" />
                  )}
                  {growth >= 0 ? "+" : ""}{growth}% vs last month
                </div>
              )}
              {!loading && monthlyData.length === 0 && (
                <span className="text-xs text-gray-400 italic">No data yet</span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="h-40 bg-gray-50 rounded-xl animate-pulse" />
          ) : monthlyData.length === 0 ? (
            <div className="h-40 flex flex-col items-center justify-center text-gray-300">
              <TrendingUp className="w-10 h-10 mb-2 opacity-40" />
              <p className="text-xs font-medium text-gray-400">
                Data will appear once tickets are created with received dates
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart
                data={monthlyData}
                margin={{ top: 5, right: 8, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="limeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#65a30d" stopOpacity={0.22} />
                    <stop offset="95%" stopColor="#65a30d" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f1f5f9"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  width={28}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    fontSize: "12px",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.08)",
                    padding: "8px 12px",
                  }}
                  formatter={(v) => [
                    <span style={{ fontWeight: 700, color: "#65a30d" }}>{v}</span>,
                    "Tickets",
                  ]}
                  labelStyle={{ fontWeight: 700, color: "#374151", marginBottom: 2 }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#65a30d"
                  strokeWidth={2.5}
                  fill="url(#limeGrad)"
                  dot={{ r: 3.5, fill: "#65a30d", strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: "#65a30d", strokeWidth: 2, stroke: "#fff" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pipeline Overview */}
        {!loading && (stats?.status_breakdown ?? []).length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
              Pipeline Overview
            </p>
            <div className="flex gap-2 flex-wrap">
              {(stats.status_breakdown)
                .sort((a, b) => b.count - a.count)
                .map((s) => (
                  <button
                    key={s._id}
                    onClick={() => navigate("/tickets")}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-lime-50 border border-gray-200 hover:border-lime-200 rounded-full text-xs font-semibold text-gray-700 hover:text-lime-700 transition-colors whitespace-nowrap"
                  >
                    <span className="w-5 h-5 bg-lime-100 text-lime-700 rounded-full flex items-center justify-center font-bold text-[10px]">
                      {s.count}
                    </span>
                    {s._id}
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Recent Tickets */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-gray-400" />
              <h2
                className="font-bold text-gray-900 text-base"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Recent Tickets
              </h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/tickets")}
              data-testid="view-all-tickets-btn"
              className="text-lime-700 border-lime-200 hover:bg-lime-50 text-xs font-bold"
            >
              View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>

          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (stats?.recent_tickets ?? []).length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-7 h-7 text-gray-300" />
              </div>
              <p className="text-gray-400 text-sm font-medium">No tickets yet</p>
              <button
                onClick={() => navigate("/tickets/new")}
                className="mt-2 text-lime-600 text-xs hover:underline font-bold"
              >
                Create your first ticket →
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {["Customer", "Ticket ID", "Charger", "Received", "Status", ""].map((h) => (
                      <th
                        key={h}
                        className="text-left px-5 py-3 text-xs font-bold uppercase tracking-widest text-gray-400"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(stats?.recent_tickets ?? []).map((t, i) => (
                    <tr
                      key={t._id}
                      data-testid={`recent-ticket-${i}`}
                      onClick={() => navigate(`/tickets/${t._id}`)}
                      className="border-b border-gray-50 hover:bg-lime-50/40 cursor-pointer transition-colors group"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}
                          >
                            {getInitials(t.customer_name)}
                          </div>
                          <span className="font-semibold text-gray-900 text-sm">
                            {t.customer_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="font-mono text-xs font-bold text-lime-700 bg-lime-50 px-2.5 py-1 rounded-lg border border-lime-100">
                          {t.ticket_id}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600">
                        {t.chargers?.[0]?.charger_model || t.charger_model || "—"}
                        {(t.chargers?.length || 0) > 1 && (
                          <span className="ml-1 text-xs text-gray-400">
                            +{t.chargers.length - 1}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(t.received_date)}
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={t.status} />
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-lime-600 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
